/**
 * 极练 Rehab & Fit —— 纯函数周报卡片与训练量汇总引擎（阶段 4b）。
 *
 * 铁律：
 * 1. 纯函数、零依赖、时间与历史一律参数传入；
 * 2. 确定性规则生成周度训练量、肌群分布、热量差额与下周行动建议；
 * 3. 严格携带 ruleVersion。
 */

export interface WeeklySessionSummaryItem {
  id: string;
  localDate: string; // YYYY-MM-DD
  status: string; // completed / ended_early
  totalVolumeKg: number;
  completedSetsCount: number;
  durationMinutes: number;
  muscleGroups: string[]; // ["胸", "肱三头肌", ...]
  progressedExercisesCount?: number;
}

export interface WeeklyWeightItem {
  localDate: string;
  weightKg: number;
}

export interface WeeklyDietItem {
  localDate: string;
  calories: number;
  proteinGrams: number;
}

export interface WeeklySummaryInput {
  referenceDate: string; // 统计截止日 YYYY-MM-DD
  targetTrainingDaysPerWeek?: number; // 默认 4 天
  dailyCalorieTarget?: number; // 默认 2200 kcal
  dailyProteinTargetGrams?: number; // 默认 140g
  sessions: WeeklySessionSummaryItem[];
  weights?: WeeklyWeightItem[];
  diets?: WeeklyDietItem[];
}

export interface MuscleVolumeBreakdown {
  muscle: string;
  sets: number;
}

export interface WeeklySummaryResult {
  period: { startDate: string; endDate: string };
  trainingDaysCount: number;
  targetTrainingDays: number;
  trainingAdherenceRate: number; // 0..1
  totalVolumeKg: number;
  totalCompletedSets: number;
  totalDurationMinutes: number;
  progressedExercisesTotal: number;
  muscleVolumeBreakdown: MuscleVolumeBreakdown[];
  averageCalories: number;
  calorieTargetDiff: number; // averageCalories - target
  averageProteinGrams: number;
  weightChangeKg: number | null;
  weightTrendSmoothKg: number | null;
  actionableAdviceZh: string;
  ruleVersion: string;
}

export const WEEKLY_SUMMARY_RULE_VERSION = "1.0.0";

/** 计算给定日期前推 6 天（共 7 天）的起始日期 */
function get7DaysStartDate(refDateStr: string): string {
  const d = new Date(`${refDateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 6);
  return d.toISOString().slice(0, 10);
}

export function calculateWeeklySummary(input: WeeklySummaryInput): WeeklySummaryResult {
  const targetDays = input.targetTrainingDaysPerWeek ?? 4;
  const calTarget = input.dailyCalorieTarget ?? 2200;
  const proteinTarget = input.dailyProteinTargetGrams ?? 140;
  const startDate = get7DaysStartDate(input.referenceDate);
  const endDate = input.referenceDate;

  // 1. 过滤 7 天周期内的有效记录
  const weekSessions = (input.sessions ?? []).filter(
    (s) => s.localDate >= startDate && s.localDate <= endDate && (s.status === "completed" || s.status === "ended_early"),
  );

  const uniqueDays = new Set(weekSessions.map((s) => s.localDate));
  const trainingDaysCount = uniqueDays.size;
  const trainingAdherenceRate = Math.min(1, Math.round((trainingDaysCount / targetDays) * 100) / 100);

  let totalVolumeKg = 0;
  let totalCompletedSets = 0;
  let totalDurationMinutes = 0;
  let progressedExercisesTotal = 0;
  const muscleMap = new Map<string, number>();

  for (const sess of weekSessions) {
    totalVolumeKg += sess.totalVolumeKg ?? 0;
    totalCompletedSets += sess.completedSetsCount ?? 0;
    totalDurationMinutes += sess.durationMinutes ?? 0;
    progressedExercisesTotal += sess.progressedExercisesCount ?? 0;

    const setsCount = sess.completedSetsCount ?? 0;
    const muscles = sess.muscleGroups ?? [];
    if (muscles.length > 0) {
      const setsPerMuscle = Math.max(1, Math.round(setsCount / muscles.length));
      for (const m of muscles) {
        muscleMap.set(m, (muscleMap.get(m) ?? 0) + setsPerMuscle);
      }
    }
  }

  totalVolumeKg = Math.round(totalVolumeKg * 10) / 10;
  const muscleVolumeBreakdown: MuscleVolumeBreakdown[] = Array.from(muscleMap.entries())
    .map(([muscle, sets]) => ({ muscle, sets }))
    .sort((a, b) => b.sets - a.sets);

  // 2. 饮食均值计算
  const weekDiets = (input.diets ?? []).filter((d) => d.localDate >= startDate && d.localDate <= endDate);
  let averageCalories = 0;
  let averageProteinGrams = 0;
  if (weekDiets.length > 0) {
    const totalCal = weekDiets.reduce((acc, d) => acc + (d.calories || 0), 0);
    const totalProt = weekDiets.reduce((acc, d) => acc + (d.proteinGrams || 0), 0);
    averageCalories = Math.round(totalCal / weekDiets.length);
    averageProteinGrams = Math.round((totalProt / weekDiets.length) * 10) / 10;
  }
  const calorieTargetDiff = averageCalories > 0 ? averageCalories - calTarget : 0;

  // 3. 体重变化与趋势
  const weekWeights = (input.weights ?? [])
    .filter((w) => w.localDate >= startDate && w.localDate <= endDate && w.weightKg > 0)
    .sort((a, b) => a.localDate.localeCompare(b.localDate));

  let weightChangeKg: number | null = null;
  let weightTrendSmoothKg: number | null = null;
  if (weekWeights.length >= 2) {
    const first = weekWeights[0]!.weightKg;
    const last = weekWeights[weekWeights.length - 1]!.weightKg;
    weightChangeKg = Math.round((last - first) * 100) / 100;
  }
  if (weekWeights.length > 0) {
    const sum = weekWeights.reduce((acc, w) => acc + w.weightKg, 0);
    weightTrendSmoothKg = Math.round((sum / weekWeights.length) * 100) / 100;
  }

  // 4. 确定性行动建议规则生成
  let advice = "";
  if (trainingDaysCount < 3) {
    advice = `本周完成 ${trainingDaysCount} 次训练（目标 ${targetDays} 次），出勤偏低。下周建议优先锁定 3 个固定训练时段，确保基本刺激。`;
  } else if (progressedExercisesTotal >= 2) {
    advice = `本周出勤达成良好（${trainingDaysCount}/${targetDays}天），共有 ${progressedExercisesTotal} 个动作达成双进阶，下周继续稳步加重。`;
  } else if (totalCompletedSets >= 30) {
    advice = `本周累计完成 ${totalCompletedSets} 组有效刺激，总容量达 ${Math.round(totalVolumeKg)}kg。下周注意监测深层疲劳与睡眠质量。`;
  } else {
    advice = `本周平稳完成 ${trainingDaysCount} 次训练，负荷控制适中。下周建议在保证余力≥2次的前提下稳步尝试上限次数。`;
  }

  if (averageCalories > 0 && Math.abs(calorieTargetDiff) >= 300) {
    if (calorieTargetDiff > 0) {
      advice += ` 饮食平均热量偏高（超出目标 ${calorieTargetDiff} kcal），建议适当微调碳水摄入。`;
    } else {
      advice += ` 饮食热量存在缺口（低于目标 ${Math.abs(calorieTargetDiff)} kcal），请确保训练后营养补给。`;
    }
  }

  if (averageProteinGrams > 0 && averageProteinGrams < proteinTarget * 0.8) {
    advice += ` 平均蛋白摄入（${averageProteinGrams}g）低于目标（${proteinTarget}g），建议增加优质蛋白补充。`;
  }

  return {
    period: { startDate, endDate },
    trainingDaysCount,
    targetTrainingDays: targetDays,
    trainingAdherenceRate,
    totalVolumeKg,
    totalCompletedSets,
    totalDurationMinutes,
    progressedExercisesTotal,
    muscleVolumeBreakdown,
    averageCalories,
    calorieTargetDiff,
    averageProteinGrams,
    weightChangeKg,
    weightTrendSmoothKg,
    actionableAdviceZh: advice,
    ruleVersion: WEEKLY_SUMMARY_RULE_VERSION,
  };
}
